import type { StructuredSearchResult } from "@storytell/streaming";
import { TbCheck, TbChevronRight, TbClock, TbDatabase, TbDatabaseOff, TbDots, TbTable } from "solid-icons/tb";
import { For, Show, createMemo, createSignal, onMount } from "solid-js";
import { twMerge } from "tailwind-merge";

interface StructuredResultsRendererProps {
  structuredResults: StructuredSearchResult;
}

interface TableRendererProps {
  headers?: string[];
  rows?: string[][];
}

const TableRenderer = (props: TableRendererProps) => {
  // Function to render markdown bold text and other formatting
  const renderCellContent = (content: string) => {
    // Replace **text** with bold HTML
    const parts = content.split(/\*\*(.+?)\*\*/);
    return (
      <>
        <For each={parts}>
          {(part, index) => (
            <Show when={index() % 2 === 1} fallback={part}>
              <strong class="font-semibold text-gray-900 dark:text-gray-100">{part}</strong>
            </Show>
          )}
        </For>
      </>
    );
  };

  const tableData = createMemo(() => ({
    headers: props.headers || [],
    rows: props.rows || [],
  }));

  return (
    <Show when={tableData().headers.length > 0}>
      <div class="overflow-x-auto rounded-lg border border-gray-200/50 dark:border-gray-700/50 shadow-sm">
        <table class="min-w-full divide-y divide-gray-200/50 dark:divide-gray-700/50">
          <thead class="bg-gradient-to-r from-gray-50 to-gray-100/50 dark:from-gray-800/30 dark:to-gray-800/10">
            <tr>
              <For each={tableData().headers}>
                {(header, index) => (
                  <th
                    scope="col"
                    class={twMerge(
                      "px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider",
                      index() === 0 && "rounded-tl-lg",
                      index() === tableData().headers.length - 1 && "rounded-tr-lg",
                    )}
                  >
                    {renderCellContent(header)}
                  </th>
                )}
              </For>
            </tr>
          </thead>
          <tbody class="bg-white/50 dark:bg-gray-900/30 divide-y divide-gray-200/30 dark:divide-gray-700/30">
            <For each={tableData().rows}>
              {(row, rowIndex) => (
                <tr
                  class="hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-transparent dark:hover:from-blue-900/20 dark:hover:to-transparent transition-all duration-200 group"
                  style={{ "animation-delay": `${rowIndex() * 50}ms` }}
                >
                  <For each={row}>
                    {(cell, cellIndex) => (
                      <td
                        class={twMerge(
                          "px-4 py-3 text-sm text-gray-700 dark:text-gray-200 group-hover:text-gray-900 dark:group-hover:text-gray-50 transition-colors",
                          rowIndex() === tableData().rows.length - 1 && cellIndex() === 0 && "rounded-bl-lg",
                          rowIndex() === tableData().rows.length - 1 &&
                            cellIndex() === row.length - 1 &&
                            "rounded-br-lg",
                        )}
                      >
                        <span class="block animate-fade-in">
                          {cell ? renderCellContent(cell) : <span class="text-gray-400 dark:text-gray-600">—</span>}
                        </span>
                      </td>
                    )}
                  </For>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </div>
    </Show>
  );
};

/**
 * Component to render a beautiful completion message for structured data queries
 */
const CompletionMessage = (props: {
  duration?: number | { seconds?: number; nanos?: number };
  assetCount?: number;
}) => {
  const [isVisible, setIsVisible] = createSignal(false);

  onMount(() => {
    setTimeout(() => setIsVisible(true), 100);
  });

  const formatDuration = (duration?: number | { seconds?: number; nanos?: number }) => {
    if (!duration) return null;

    // Handle protobuf Duration object
    let seconds: number;
    if (typeof duration === "object" && "seconds" in duration) {
      seconds = Number(duration.seconds || 0);
    } else {
      seconds = Number(duration);
    }

    if (isNaN(seconds)) return null;
    if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
    return `${seconds.toFixed(1)}s`;
  };

  return (
    <div
      class={twMerge(
        "flex items-center gap-3 p-3 rounded-lg border",
        "bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20",
        "border-green-200/50 dark:border-green-700/50",
        "transition-all duration-500 transform",
        isVisible() ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2",
      )}
    >
      <div class="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 dark:bg-green-800/30">
        <TbCheck class="w-4 h-4 text-green-600 dark:text-green-400 animate-scale-in" />
      </div>
      <div class="flex-1">
        <p class="text-sm font-medium text-gray-900 dark:text-gray-100">Successfully queried structured data</p>
        <div class="flex items-center gap-3 mt-1">
          <Show when={props.assetCount}>
            {(count) => (
              <span class="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                <TbDatabase class="w-3 h-3" />
                {count()} {count() === 1 ? "source" : "sources"}
              </span>
            )}
          </Show>
          <Show when={props.duration}>
            {(duration) => (
              <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-800/30 text-xs font-medium text-green-700 dark:text-green-300">
                <TbClock class="w-3 h-3" />
                {formatDuration(duration())}
              </span>
            )}
          </Show>
        </div>
      </div>
    </div>
  );
};

/**
 * Empty state component for when no results are found
 */
const EmptyState = (props: { hasExecutedQuery?: boolean }) => {
  // If we have headers but no rows, it means the query executed successfully but returned 0 results
  const isValidEmptyResult = () => props.hasExecutedQuery;

  return (
    <div class="flex flex-col items-center justify-center py-8 px-4 text-center">
      <div class="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
        <TbDatabaseOff class="w-6 h-6 text-gray-400 dark:text-gray-600" />
      </div>
      <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
        {isValidEmptyResult() ? "No matching records found" : "No structured data found"}
      </h3>
      <p class="text-xs text-gray-500 dark:text-gray-400 max-w-xs">
        {isValidEmptyResult()
          ? "The query executed successfully but no records matched your search criteria."
          : "No matching results were found in the available CSV or spreadsheet data sources."}
      </p>
    </div>
  );
};

/**
 * Renders structured SQL query results in an expandable table format with delightful animations
 */
export const StructuredResultsRenderer = (props: StructuredResultsRendererProps) => {
  const [isExpanded, setIsExpanded] = createSignal(false);
  const [expandedQueries, setExpandedQueries] = createSignal<Record<number, boolean>>({});
  const [showContent, setShowContent] = createSignal(false);

  const resultCount = () => props.structuredResults.rowCount || props.structuredResults.rows?.length || 0;

  // Check if data is truncated (backend only sent first 15 rows)
  const isTruncated = createMemo(() => {
    const totalRows = props.structuredResults.rowCount || 0;
    const displayedRows = props.structuredResults.rows?.length || 0;
    return totalRows > displayedRows;
  });

  // Generate row count display text
  const rowCountText = createMemo(() => {
    const totalRows = resultCount();
    const displayedRows = props.structuredResults.rows?.length || 0;

    if (isTruncated()) {
      return `showing first ${displayedRows} of ${totalRows} rows`;
    } else {
      return `${totalRows} row${totalRows !== 1 ? "s" : ""}`;
    }
  });

  // Check if we have query index (indicates multiple queries)
  const hasQueryIndex = createMemo(() => {
    return props.structuredResults.queryIndex !== undefined && props.structuredResults.totalQueries !== undefined;
  });

  const toggleQueryExpanded = (index: number) => {
    setExpandedQueries((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  // Check if this is a completion message
  const isCompletionMessage = createMemo(() => {
    return (
      props.structuredResults.isCompletion ||
      (props.structuredResults.assetName === "Completion Summary" &&
        !props.structuredResults.rows?.length &&
        !props.structuredResults.markdownTable)
    );
  });

  // Parse duration and asset count from completion message if present
  const completionData = createMemo(() => {
    return {
      duration: props.structuredResults.duration,
      assetCount: props.structuredResults.assetCount,
    };
  });

  onMount(() => {
    if (!isCompletionMessage()) {
      setTimeout(() => setShowContent(true), 100);
    }
  });

  return (
    <div class="space-y-3">
      <Show
        when={!isCompletionMessage()}
        fallback={<CompletionMessage duration={completionData().duration} assetCount={completionData().assetCount} />}
      >
        {/* Header with database icon */}
        <div
          class={twMerge(
            "flex items-center gap-2 transition-all duration-500 transform",
            showContent() ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2",
          )}
        >
          <div class="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-900/30">
            <TbDatabase class="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 class="text-sm text-gray-700 dark:text-gray-300">
            Results from{" "}
            <strong class="font-semibold text-gray-900 dark:text-gray-100">{props.structuredResults.assetName}</strong>
          </h3>
        </div>

        {/* Display query reasoning if available */}
        <Show when={props.structuredResults.queryReason}>
          <div
            class={twMerge(
              "text-sm text-primary-text leading-relaxed transition-all duration-500 transform",
              showContent() ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2",
            )}
          >
            {props.structuredResults.queryReason}
          </div>
        </Show>

        {/* Expandable results section */}
        <Show when={resultCount() > 0}>
          <button
            type="button"
            onClick={() => {
              setIsExpanded((prev) => !prev);
              if (!showContent()) setShowContent(true);
            }}
            class={twMerge(
              "group/btn flex items-center gap-2 px-3 py-1.5 rounded-lg",
              "bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700",
              "hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600",
              "transition-all duration-200 cursor-pointer",
              showContent() ? "opacity-100" : "opacity-0",
            )}
            style={{ "animation-delay": "200ms" }}
          >
            <p class="text-sm text-blue-600 dark:text-blue-400 font-medium">
              <Show
                when={hasQueryIndex()}
                fallback={
                  <>
                    Found {resultCount()} result{resultCount() !== 1 ? "s" : ""}
                  </>
                }
              >
                <>
                  Result {props.structuredResults.queryIndex} of {props.structuredResults.totalQueries}
                </>
              </Show>
            </p>

            <TbChevronRight
              class="size-3.5 stroke-[2.5] text-gray-400 dark:text-gray-500 transition-transform duration-200"
              classList={{ "rotate-90": isExpanded() }}
            />
          </button>

          <Show when={isExpanded()}>
            <div
              class="mt-3 space-y-3 animate-slide-down"
              style={{
                animation: "slideDown 0.3s ease-out forwards",
                "transform-origin": "top",
              }}
            >
              {/* Asset information card */}
              <div class="relative overflow-hidden border border-gray-200/50 dark:border-gray-700/50 rounded-xl p-4 bg-gradient-to-br from-white to-gray-50/50 dark:from-gray-800/50 dark:to-gray-900/30 hover:shadow-lg dark:hover:shadow-gray-900/50 transition-all duration-300 group">
                <div class="flex items-start gap-3">
                  <div class="w-10 h-10 bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-800/30 dark:to-emerald-800/30 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <TbTable class="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>

                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 mb-2">
                      <h4 class="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate">
                        {props.structuredResults.assetName}
                      </h4>
                      <span class="text-xs text-gray-600 dark:text-gray-400 bg-gradient-to-r from-gray-100 to-gray-200/50 dark:from-gray-700 dark:to-gray-800/50 px-2.5 py-1 rounded-full font-medium">
                        CSV/Spreadsheet
                      </span>
                    </div>

                    {/* Display query results - using raw data from backend */}
                    <Show when={hasQueryIndex()}>
                      <div class="mb-2">
                        <p class="text-xs font-medium text-gray-600 dark:text-gray-400">
                          Query {props.structuredResults.queryIndex} of {props.structuredResults.totalQueries}
                        </p>
                      </div>
                    </Show>

                    <Show when={props.structuredResults.headers && props.structuredResults.rows}>
                      <div class="mt-4">
                        <p class="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">
                          Query Results ({rowCountText()})
                        </p>
                        <TableRenderer
                          headers={props.structuredResults.headers}
                          rows={props.structuredResults.rows || []}
                        />

                        {/* Truncation indicator */}
                        <Show when={isTruncated()}>
                          <div class="mt-3 flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50/50 dark:bg-gray-800/30 rounded-md px-3 py-2">
                            <span>
                              Showing first {props.structuredResults.rows?.length || 0} of {resultCount()} total rows
                            </span>
                          </div>
                        </Show>
                      </div>
                    </Show>

                    {/* Fallback for old markdown format */}
                    <Show when={props.structuredResults.markdownTable && !props.structuredResults.headers}>
                      <div class="mt-4">
                        <p class="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">
                          Query Results
                        </p>
                        <TableRenderer headers={[]} rows={[]} />
                      </div>
                    </Show>
                  </div>
                </div>
              </div>
            </div>
          </Show>
        </Show>

        {/* No results message with beautiful empty state */}
        <Show when={resultCount() === 0 && !isCompletionMessage()}>
          <EmptyState
            hasExecutedQuery={!!props.structuredResults.headers && props.structuredResults.headers.length > 0}
          />
        </Show>
      </Show>
    </div>
  );
};
